import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { ChevronDown, Search, Star, Globe, Loader2 } from "lucide-react";

// module-level cache so the 1,200+ league catalogue is fetched only once per session
let _catalogue = null;
let _inflight = null;
const loadCatalogue = () => {
  if (_catalogue) return Promise.resolve(_catalogue);
  if (!_inflight) {
    _inflight = api.get("/leagues?all=true").then((r) => { _catalogue = r.data || []; return _catalogue; })
      .catch(() => { _catalogue = []; return _catalogue; });
  }
  return _inflight;
};

export const LeagueSelect = ({ value, onChange, className = "", testId = "league-select", allowAll = false }) => {
  const [leagues, setLeagues] = useState(_catalogue || []);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(!_catalogue);
  const boxRef = useRef(null);

  useEffect(() => { loadCatalogue().then((c) => { setLeagues(c); setLoading(false); }); }, []);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const current = leagues.find((l) => l.id === Number(value));
  const isAll = allowAll && (value === "all" || value === undefined || value === null);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return leagues;
    return leagues.filter((l) => (l.name || "").toLowerCase().includes(t) || (l.country || "").toLowerCase().includes(t));
  }, [q, leagues]);

  // group: featured first, then by country
  const groups = useMemo(() => {
    const feat = filtered.filter((l) => l.featured);
    const rest = {};
    filtered.filter((l) => !l.featured).forEach((l) => {
      const c = l.country || "World";
      (rest[c] = rest[c] || []).push(l);
    });
    return { feat, rest };
  }, [filtered]);

  const pick = (id) => { onChange(Number(id)); setOpen(false); setQ(""); };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <button
        type="button" data-testid={testId} aria-haspopup="listbox" onClick={() => setOpen((o) => !o)}
        className="flex w-full min-w-[220px] items-center justify-between gap-2 rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-3 text-sm text-white outline-none transition hover:border-cyan-400/40 focus:border-cyan-400/50"
      >
        <span className="flex items-center gap-2 truncate">
          {current?.flag ? <img src={current.flag} alt="" className="h-3.5 w-5 rounded-sm object-cover" /> : <Globe size={14} className="text-white/40" />}
          <span className="truncate">{current ? current.name : "Select league"}</span>
          {current?.country && !current.featured && <span className="text-white/35">· {current.country}</span>}
        </span>
        <ChevronDown size={15} className={`shrink-0 text-white/40 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-[300px] overflow-hidden rounded-xl border border-white/12 bg-[#0b0b0d] shadow-[0_20px_60px_rgba(0,0,0,0.7)]" data-testid={`${testId}-panel`}>
          <div className="flex items-center gap-2 border-b border-white/8 px-3 py-2">
            <Search size={14} className="text-white/40" />
            <input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search 1,200+ leagues or a country…"
              data-testid={`${testId}-search`}
              className="w-full bg-transparent text-sm text-white placeholder:text-white/30 outline-none"
            />
          </div>
          <div className="max-h-[340px] overflow-y-auto py-1">
            {loading ? (
              <p className="flex items-center gap-2 px-3 py-4 text-xs text-white/40"><Loader2 size={13} className="animate-spin" /> Loading worldwide catalogue…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-white/40">No league matches “{q}”.</p>
            ) : (
              <>
                {groups.feat.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 px-3 pb-1 pt-2 font-tac text-[8px] tracking-widest text-cyan-300/70"><Star size={9} /> FEATURED</p>
                    {groups.feat.map((l) => (
                      <Row key={l.id} l={l} active={l.id === Number(value)} onPick={pick} />
                    ))}
                  </div>
                )}
                {Object.keys(groups.rest).sort().map((country) => (
                  <div key={country}>
                    <p className="px-3 pb-1 pt-2 font-tac text-[8px] tracking-widest text-white/35">{country.toUpperCase()}</p>
                    {groups.rest[country].map((l) => (
                      <Row key={l.id} l={l} active={l.id === Number(value)} onPick={pick} />
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ l, active, onPick }) => (
  <button
    type="button" onClick={() => onPick(l.id)} data-testid={`league-option-${l.id}`}
    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] transition hover:bg-white/5 ${active ? "bg-cyan-400/10 text-cyan-200" : "text-white/80"}`}
  >
    {l.flag ? <img src={l.flag} alt="" className="h-3 w-4.5 rounded-sm object-cover" /> : l.logo ? <img src={l.logo} alt="" className="h-3.5 w-3.5 object-contain" /> : <Globe size={12} className="text-white/30" />}
    <span className="truncate">{l.name}</span>
    {l.type === "Cup" && <span className="ml-auto rounded-sm bg-white/5 px-1.5 text-[8px] text-white/40">CUP</span>}
  </button>
);
